import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack, Alert, AlertColor } from "@mui/material";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { useWorkflows } from "../components/data/useWorkflows";
import { MessageToast, ToastMode } from "../components/shared/MessageToast";
import { Snackbar } from "../components/shared/Snackbar";
import { Customer } from "../components/shared/interfaces/Customer";
import { CustomersByEntityDropdown } from "../components/CustomersByEntityDropdown";
import { WorkflowGrid } from "../components/workflows/WorkflowGrid";
import { WorkflowCreate } from "../components/workflows/WorkflowCreate";

import RouteBreadcrumbs from "../components/shared/RouteBreadcrumbs";

import { ReusableCustomQuestionLibrary } from "../components/workflows/ReusableCustomQuestions/ReusableCustomQuestionLibrary";
import { useCompaniesByCustomerId } from "../components/data/useCompaniesByCustomerId";
import { useSitesByCompanyId } from "../components/data/useSitesByCompanyId";
import { PersistedCompanyDropdown } from "../components/PersistedCompanyDropdown";
import { PersistedSiteDropdown } from "../components/PersistedSiteDropdown";

interface LocationState {
  deletedWorkflowId?: string;
}

export function Workflows() {
  const { t } = useTranslation();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | undefined>();
  const [selectedSiteName, setSelectedSiteName] = useState<string | undefined>();

  const companies = useCompaniesByCustomerId(selectedCustomer?.id);

  const companyId = useMemo(() => {
    if (companies.data && selectedCompanyName) {
      return companies.data?.find((c) => c.name === selectedCompanyName)?.id;
    }
  }, [companies.data, selectedCompanyName]);

  const sites = useSitesByCompanyId(companyId);

  const siteId = useMemo(() => {
    if (sites.data && selectedSiteName) {
      return sites.data?.find((c) => c.name === selectedSiteName)?.id;
    }
  }, [sites.data, selectedSiteName]);

  const workflows = useWorkflows(selectedCustomer && selectedCustomer.id, companyId, siteId);

  const [errorToast, setErrorToast] = useState({
    show: false,
    mode: ToastMode.Error,
    text: "",
  });

  const [workflowCreateDialogOpen, setWorkflowCreateDialogOpen] = useState(false);

  const [reusableCustomQuestionDialogOpen, setReusableCustomQuestionDialogOpen] = useState(false);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  useEffect(() => {
    if (workflows.error) {
      setErrorToast({
        text: t("common.dataFetchError", { val: "$t(workflows.workflows)" }),
        mode: ToastMode.Error,
        show: true,
      });
    } else {
      setErrorToast({
        text: "",
        mode: ToastMode.Error,
        show: false,
      });
    }
  }, [workflows.error, t]);

  useEffect(() => {
    if (locationState?.deletedWorkflowId) {
      setSnackbarText(
        t("workflows.workflowDeleted", {
          val: locationState.deletedWorkflowId,
        }),
      );
      setSnackbarSeverity("success");
      setShowSnackbar(true);

      //Clear the deleted workflow ID from location state so that the success snackbar only appears once
      window.history.replaceState({ deletedWorkflowId: undefined }, document.title);
    }
  }, [locationState, t]);

  const handleCustomerSelect = useCallback((customer?: Customer) => {
    setSelectedCustomer(customer);
  }, []);

  const handleCompanyChange = useCallback((selectedCompanyName: string) => {
    setSelectedCompanyName(selectedCompanyName);
  }, []);

  const handleSiteChange = useCallback((siteName: string) => {
    setSelectedSiteName(siteName);
  }, []);

  function openWorkflowCreate() {
    setWorkflowCreateDialogOpen(true);
  }

  function closeWorkflowCreate() {
    setWorkflowCreateDialogOpen(false);
  }

  function handleWorkflowCreated() {
    setWorkflowCreateDialogOpen(false);
    workflows.refetch();
  }

  function handleSnackbarClose() {
    setShowSnackbar(false);
  }

  return (
    <React.Fragment>
      <Box marginBottom={2}>
        <RouteBreadcrumbs />
      </Box>

      <Stack direction="row">
        <CustomersByEntityDropdown
          selectedCustomerId={selectedCustomer?.id}
          entity="workflow"
          onSelect={handleCustomerSelect}
        />
        <PersistedCompanyDropdown customerId={selectedCustomer?.id} onCompanyChange={handleCompanyChange} showLabel />
        <PersistedSiteDropdown
          companyId={companyId}
          customerId={selectedCustomer?.id}
          onSiteChange={handleSiteChange}
          showLabel
        />
      </Stack>
      <div>
        {selectedCustomer && (
          <>
            <Button onClick={() => setReusableCustomQuestionDialogOpen(true)} startIcon={<LibraryBooksIcon />}>
              {t("customQuestionLibrary.customQuestionLibrary")}
            </Button>
          </>
        )}
      </div>

      {errorToast.show ? (
        <div className="w-100 d-flex justify-content-center pt-2">
          <MessageToast text={errorToast.text} mode={errorToast.mode} show={errorToast.show} />
        </div>
      ) : (
        selectedCustomer && (
          <Box marginTop={2}>
            <WorkflowGrid
              workflowData={workflows.data}
              workflowDataLoading={workflows.isLoading}
              workflowDataFetching={workflows.isFetching}
              onDataRefreshClick={workflows.refetch}
              onNewWorkflowClick={openWorkflowCreate}
              customerId={selectedCustomer?.id}
            />
            <WorkflowCreate
              customerId={selectedCustomer?.id}
              open={workflowCreateDialogOpen}
              onClose={closeWorkflowCreate}
              onCreatedSuccess={handleWorkflowCreated}
            />
          </Box>
        )
      )}
      <Snackbar open={showSnackbar} onClose={handleSnackbarClose}>
        <Alert severity={snackbarSeverity} variant="filled">
          {snackbarText}
        </Alert>
      </Snackbar>
      {reusableCustomQuestionDialogOpen && selectedCustomer && (
        <ReusableCustomQuestionLibrary
          customerId={selectedCustomer.id}
          open={reusableCustomQuestionDialogOpen}
          onClose={() => setReusableCustomQuestionDialogOpen(false)}
        />
      )}
    </React.Fragment>
  );
}
